/** Deterministic participant accent color (shared contract with iOS VidaWaveCore). */
/** FNV-1a 32-bit — must match `WaveParticipantColor.fnv1aHash` on iOS. */
export function fnv1aHash(input) {
    let hash = 2166136261;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}
const PARTICIPANT_PALETTE = [
    { hex: "#E53935", hsl: { h: 1, s: 77, l: 55 } },
    { hex: "#D81B60", hsl: { h: 336, s: 78, l: 48 } },
    { hex: "#8E24AA", hsl: { h: 291, s: 64, l: 42 } },
    { hex: "#5E35B1", hsl: { h: 262, s: 52, l: 47 } },
    { hex: "#3949AB", hsl: { h: 231, s: 48, l: 45 } },
    { hex: "#1E88E5", hsl: { h: 207, s: 78, l: 51 } },
    { hex: "#039BE5", hsl: { h: 199, s: 98, l: 45 } },
    { hex: "#00ACC1", hsl: { h: 187, s: 100, l: 38 } },
    { hex: "#00897B", hsl: { h: 174, s: 100, l: 27 } },
    { hex: "#43A047", hsl: { h: 122, s: 39, l: 45 } },
    { hex: "#F4511E", hsl: { h: 14, s: 90, l: 54 } },
    { hex: "#FB8C00", hsl: { h: 36, s: 100, l: 49 } },
];
export function normalizedEmail(email) {
    const trimmed = (email ?? "").trim().toLowerCase();
    return trimmed.length > 0 ? trimmed : null;
}
export function participantColor(sub) {
    const index = fnv1aHash(sub) % PARTICIPANT_PALETTE.length;
    return PARTICIPANT_PALETTE[index];
}
/** Deterministic accent from normalized email; falls back to `sub` when email is absent. */
export function participantColorByEmail(email, fallbackSub) {
    const key = normalizedEmail(email) ?? fallbackSub ?? "anonymous";
    return participantColor(key);
}
export function participantColorCssVar(sub) {
    return participantColor(sub).hex;
}
/** Human-readable label from email local-part (e.g. `mario.rossi@…` → `Mario.rossi`). */
export function displayNameFromEmail(email) {
    const trimmed = email.trim();
    if (!trimmed)
        return email;
    const local = trimmed.split("@")[0] ?? trimmed;
    if (!local)
        return trimmed;
    return local.charAt(0).toUpperCase() + local.slice(1).toLowerCase();
}
/** Initials from email local-part (splits on `.`, `_`, `-`). */
export function initialsFromEmail(email) {
    const trimmed = email.trim();
    if (!trimmed)
        return "?";
    const local = trimmed.split("@")[0] ?? trimmed;
    const parts = local
        .split(/[._-]+/)
        .map((p) => p.trim())
        .filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
    }
    return local.slice(0, 2).toUpperCase();
}
export function initialsFromLabel(label, email) {
    const resolvedEmail = normalizedEmail(email ?? (label.includes("@") ? label : null));
    if (resolvedEmail)
        return initialsFromEmail(resolvedEmail);
    const trimmed = label.trim();
    if (!trimmed)
        return "?";
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
    }
    return trimmed.slice(0, 2).toUpperCase();
}
